<template>
<link v-if="isUmbraco" rel="stylesheet" href="/assets/index.css" />

  <Transition
    appear
    :enter-active-class="activeClass"
    leave-active-class="transition-all duration-300 ease-out-sine"
    enter-from-class="opacity-0 translate-y-10"
    leave-to-class="opacity-0 translate-y-10"
    :key="`${title}-${index}`"
  >
    <div :class="styles">
      <div v-if="image" :class="imageStyles">
        <image-component
          :alt="imgAlt"
          :image="image"
          isLink
          loading="lazy"
          classes="w-full h-full object-cover"
        />
      </div>
      <div :class="textStyles">
        <div v-if="date || location" class="flex mt-6 md:mt-8">
          <paragraph
            v-if="date"
            classes="font-bold mr-6 capitalize"
            :text="formattetDate"
            :is-umbraco="isUmbraco"
            :theme="themeType"
          />
          <paragraph
            v-if="location"
            classes="hidden font-bold capitalize md:block mb-0"
            :text="location"
            :is-umbraco="isUmbraco"
            :theme="themeType"
          />
        </div>
        <header3
          :text="title"
          :is-umbraco="isUmbraco"
          :theme="themeType"
          classes="mt-1 md:mt-2"
        />
        <div v-if="tag" class="text-aubergine border-aubergine-40 tag mt-5 self-start">
          {{ tag }}
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { toRefs, computed } from "vue";
import Header3 from "@components/Typography/Header3.ce.vue";
import Paragraph from "@components/Typography/Paragraph.ce.vue";
import ImageComponent from "@atoms/Image/Img.vue";
import { Theme } from "@/types/types";
import { getThemeType, formatDate } from "@/util/helper";

export interface NewCardProps {
  image: string;
  imgAlt?: string;
  location?: string;
  date?: string;
  title: string;
  isHorizontal?: boolean;
  href?: string;
  target?: string;
  theme?: Theme | null;
  isUmbraco?: boolean;
  classes?: string;
  tag?: string;
  index: number;
}

const props = withDefaults(defineProps<NewCardProps>(), {
  imgAlt: "",
  isHorizontal: false,
  isUmbraco: false,
});

const {
  title,
  imgAlt,
  image,
  location,
  date,
  isHorizontal,
  theme,
  isUmbraco,
  classes,
  index,
} = toRefs(props);

const formattetDate = computed(() => {
  if (!date?.value) return "";
  return formatDate(date.value, "long");
});

const styles = computed(() => {
  return isHorizontal.value
    ? `flex flex-col md:grid md:grid-cols-12 md:content-evenly ${classes?.value}`
    : `flex flex-col ${classes?.value}`;
});

const imageStyles = computed(() => {
  return isHorizontal.value
    ? "col-span-6 overflow-hidden aspect-card"
    : "w-full h-full overflow-hidden aspect-card ";
});

const textStyles = computed(() => {
  return isHorizontal.value
    ? "flex flex-col md:col-start-8 md:col-span-5 md:justify-center"
    : "flex flex-col max-w-[90%] md:max-w-full";
});

const themeType = computed(() => {
  if (!theme?.value) return "dark";
  return getThemeType(theme.value);
});

const activeClass = computed(() => {
  const delayTime = index.value * 100;
  const delay = `delay-[${delayTime}ms]`;
  return `transition duration-500 ease-out-sine ${delay}`;
});
</script>
